import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SlotAttendanceParams } from '../../api/repository/slot-attendance';
import { EmptyState, Modal, Spinner, Toast } from '../../components';
import { useQltSlotAttendanceStore } from '../../stores/qlt';
import { AttendanceRow } from './AttendanceRow';
import { classLabel } from '../periodLabels';
import { ClipboardCheckIcon } from '../../layout/icons';
import '../../styles/class-management.css';

export interface AttendanceModalProps {
  open: boolean;
  classTitle: string;
  context: SlotAttendanceParams;
  onClose: () => void;
}

const WEEKDAY_KEYS = ['weekday.sun', 'weekday.mon', 'weekday.tue', 'weekday.wed', 'weekday.thu', 'weekday.fri', 'weekday.sat'] as const;

function formatDate(date: string): string {
  const parts = date.slice(0, 10).split('-');
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function weekdayKey(date: string): (typeof WEEKDAY_KEYS)[number] | null {
  return WEEKDAY_KEYS[new Date(`${date.slice(0, 10)}T00:00:00Z`).getUTCDay()] || null;
}

function SearchIcon() {
  return <svg className="cm-search__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden={true}><circle cx="7" cy="7" r="4.6" fill="none" stroke="currentColor" strokeWidth="1.5" /><line x1="10.6" y1="10.6" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>;
}

function CheckIcon() {
  return <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden={true}><path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export function AttendanceModal({ open, classTitle, context, onClose }: AttendanceModalProps) {
  const { t } = useTranslation(['panels', 'common']);
  const weekday = weekdayKey(context.date);
  const attendance = useQltSlotAttendanceStore();
  const [query, setQuery] = useState('');
  const [savedToast, setSavedToast] = useState(false);

  // The present default is applied by the store inside load(), so nothing here can race it away.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    void attendance.load(context);
  }, [open, context.class, context.indexDay, context.date, attendance.load]);

  const normalized = query.trim().toLocaleLowerCase('vi-VN');
  const rows = normalized ? attendance.rows.filter((row) => `${row.student.name || ''} ${row.student.code || ''}`.toLocaleLowerCase('vi-VN').indexOf(normalized) >= 0) : attendance.rows;

  function markAll() {
    attendance.markAllPresent();
  }

  // the save's reload lands in this modal, so it stays open until the answer is back
  function close() {
    if (!attendance.saving) onClose();
  }

  async function save() {
    const ok = await attendance.save();
    if (ok) {
      setSavedToast(true);
      onClose();
    }
  }

  const counts = attendance.counts;
  const actions = (
    <>
      <div className="cm-legend">
        <span><i className="cm-legend__dot" style={{ background: 'var(--color-present)' }} /><strong>{counts.onTime}</strong> {t('attendance.legend.onTime')}</span>
        <span><i className="cm-legend__dot" style={{ background: 'var(--color-warning)' }} /><strong>{counts.late}</strong> {t('attendance.legend.late')}</span>
        <i className="cm-legend__rule" aria-hidden={true} />
        <span><i className="cm-legend__dot" style={{ background: 'var(--color-orange)' }} /><strong>{counts.excused}</strong> {t('attendance.legend.excused')}</span>
        <span><i className="cm-legend__dot" style={{ background: 'var(--color-danger-alt)' }} /><strong>{counts.unexcused}</strong> {t('attendance.legend.unexcused')}</span>
      </div>
      <button type="button" className="cm-btn-primary" disabled={attendance.saving || attendance.rows.length === 0} onClick={() => void save()}>{attendance.saving ? t('common:states.saving') : t('attendance.save')}</button>
    </>
  );

  return (
    <>
      <Modal open={open} title={t('attendance.title')} description={`${classLabel(classTitle)} - ${weekday ? t(weekday) : ''}, ${formatDate(context.date)}`} className="cm-modal cm-attendance-modal" actions={actions} onClose={close}>
        {/* edits made during the save would be wiped by its reload, so the roster is locked */}
        <fieldset className="cm-attendance-lock" disabled={attendance.saving}>
        <div className="cm-attendance-tools">
          <div className="cm-search">
            <SearchIcon />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('attendance.searchPlaceholder')} aria-label={t('attendance.searchLabel')} />
          </div>
          <button type="button" className="cm-mark-all" onClick={markAll}><CheckIcon /> {t('attendance.markAll')}</button>
        </div>
        {attendance.loading && attendance.rows.length === 0 ? <Spinner label={t('attendance.loadingRoster')} /> : null}
        {!attendance.loading && attendance.rows.length === 0 ? <EmptyState icon={<ClipboardCheckIcon size={26} />} title={t('attendance.empty.title')} description={t('attendance.empty.description')} /> : null}
        {rows.length > 0 ? (
          <ul className="cm-roster">
            {rows.map((row) => (
              <AttendanceRow
                key={row._id}
                row={row}
                value={attendance.draft[row._id] || { status: row.status, subStatus: row.subStatus }}
                onChange={(value) => attendance.setDraft(row._id, value)}
              />
            ))}
          </ul>
        ) : null}
        </fieldset>
      </Modal>
      <Toast open={savedToast} message={t('attendance.saved')} onClose={() => setSavedToast(false)} />
      <Toast open={Boolean(attendance.error)} kind="error" message={attendance.error || ''} duration={0} onRetry={() => void save()} onClose={attendance.clearError} />
    </>
  );
}
